import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useUserContext } from './UserContext';
import axiosInstance from '@/utils/axiosInstance';
import { Integration } from '@/interface/entityInterface';
import { CallsFlowControl } from '@/libraries/softphone/CallsFlowControl';
import type { SoftPhoneConfig } from '@/libraries/softphone/types';
import { getSipErrorInfo } from '@/utils/sipErrors';
import { toast } from 'sonner';

export interface SipConnectionStatus {
  isConnecting: boolean;
  isConnected: boolean;
  isRegistered: boolean;
  error: string | null;
  connectionAttempts: number;
}

export interface SipContextType {
  // Connection status
  status: SipConnectionStatus;

  // SIP credentials and config
  sipConfig: SoftPhoneConfig | null;
  username: string;
  apiKey: string;
  recordingUrl: string;

  // FreePBX availability
  hasFreePBX: boolean;

  // Control methods
  connect: () => void;
  disconnect: () => void;
  refreshCredentials: () => void;

  // Call management (from your softphone library)
  flowControl: CallsFlowControl | null;

  // Call actions
  makeCall: (number: string) => void;
  endCall?: () => void;
  answerCall?: (sessionId: string) => void;
  rejectCall?: (sessionId: string) => void;

  // Audio controls
  setCallVolume: (volume: number) => void;
  setRingVolume: (volume: number) => void;
  initializeAudio: () => Promise<void>;

  // Loading states
  isLoadingCredentials: boolean;
  credentialsError: string | null;

  // Call controls
  mute: () => void;
  unmute: () => void;
  toggleMute?: () => void;
  hold: () => void;
  unhold: () => void;
  holdCall?: (sessionId: string) => void;
  unholdCall?: (sessionId: string) => void;
}

const SipContext = createContext<SipContextType | undefined>(undefined);

export const useSipContext = () => {
  const context = useContext(SipContext);
  if (!context) {
    // Return default values when SipContext is not available
    return {
      status: {
        isConnecting: false,
        isConnected: false,
        isRegistered: false,
        error: null,
        connectionAttempts: 0,
      },
      sipConfig: null,
      username: '',
      apiKey: '',
      recordingUrl: '',
      hasFreePBX: false,
      connect: () => {},
      disconnect: () => {},
      refreshCredentials: () => {},
      flowControl: null,
      makeCall: () => {},
      endCall: () => {},
      answerCall: () => {},
      rejectCall: () => {},
      setCallVolume: () => {},
      setRingVolume: () => {},
      initializeAudio: async () => {},
      isLoadingCredentials: false,
      credentialsError: null,
      mute: () => {},
      unmute: () => {},
      toggleMute: () => {},
      hold: () => {},
      unhold: () => {},
      holdCall: () => {},
      unholdCall: () => {}
    };
  }
  return context;
};

export const SipContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { decodedToken } = useUserContext();
  const flowControlRef = useRef<CallsFlowControl | null>(null);

  // Connection status state
  const [status, setStatus] = useState<SipConnectionStatus>({
    isConnecting: false,
    isConnected: false,
    isRegistered: false,
    error: null,
    connectionAttempts: 0,
  });

  // SIP configuration state
  const [sipConfig, setSipConfig] = useState<SoftPhoneConfig | null>(null);
  const [username, setUsername] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [recordingUrl, setRecordingUrl] = useState('');
  const [hasFreePBX, setHasFreePBX] = useState(false);

  // Loading states
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);

  // Initialize flow control
  useEffect(() => {
    if (!flowControlRef.current) {
      flowControlRef.current = new CallsFlowControl();

      // Set up audio elements for the flow control
      const setupAudio = () => {
        try {
          // Create ringback tone (for outgoing calls)
          const ringbackTone = new Audio('/sound/ringback.ogg');
          ringbackTone.loop = true;
          ringbackTone.volume = 0.6;
          ringbackTone.preload = 'auto';
          flowControlRef.current!.ringbackTone = ringbackTone;

          // Create ringtone element (for incoming calls)
          const ringer = new Audio('/sound/ringtone.ogg');
          ringer.loop = true;
          ringer.volume = 0.6;
          ringer.preload = 'auto';
          flowControlRef.current!.ringer = { current: ringer };

          // Create player element (for call audio)
          const player = new Audio();
          player.autoplay = true;
          player.volume = 0.8;
          player.muted = false;
          player.controls = false;
          player.preload = 'auto';

          // Add additional audio attributes for better compatibility
          player.setAttribute('playsinline', 'true');
          player.setAttribute('webkit-playsinline', 'true');

          flowControlRef.current!.player = { current: player };

          console.log('Audio player element created with settings:', {
            autoplay: player.autoplay,
            volume: player.volume,
            muted: player.muted,
            preload: player.preload
          });

          console.log('Audio elements set up for SIP context');

          // Test audio loading
          ringbackTone.addEventListener('canplaythrough', () => {
            console.log('Ringback tone loaded successfully');
          });
          ringer.addEventListener('canplaythrough', () => {
            console.log('Ringtone loaded successfully');
          });

          // Handle audio errors
          ringbackTone.addEventListener('error', (e) => {
            console.error('Error loading ringback tone:', e);
          });
          ringer.addEventListener('error', (e) => {
            console.error('Error loading ringtone:', e);
          });

        } catch (error) {
          console.error('Error setting up audio:', error);
        }
      };

      setupAudio();

      // Set up engine events
      flowControlRef.current.engineEvent = (event, payload) => {
        console.log(`SIP Engine Event: ${event}`, payload);

        switch (event) {
          case 'connecting':
            setStatus(prev => ({ ...prev, isConnecting: true, error: null }));
            break;
          case 'connected':
            setStatus(prev => ({
              ...prev,
              isConnecting: false,
              isConnected: true,
              error: null
            }));
            break;
          case 'registered':
            setStatus(prev => ({
              ...prev,
              isRegistered: true,
              error: null
            }));
            break;
          case 'disconnected':
            setStatus(prev => ({
              ...prev,
              isConnecting: false,
              isConnected: false,
              isRegistered: false
            }));
            break;
          case 'registrationFailed': {
            console.error('SIP Registration Failed:', payload);

            // Get user-friendly error message
            const errorInfo = getSipErrorInfo('registration failed');

            // Show user-friendly toast notification
            toast.error(errorInfo.title, {
              description: `${errorInfo.message}${errorInfo.action ? '\n\n' + errorInfo.action : ''}`,
              duration: 8000,
            });

            setStatus(prev => ({
              ...prev,
              isConnecting: false,
              error: errorInfo.message,
              connectionAttempts: prev.connectionAttempts + 1
            }));
            break;
          }
          // Note: 'connectionFailed' may not be a standard event type in CallsFlowControl
          // case 'connectionFailed':
          //   setStatus(prev => ({
          //     ...prev,
          //     isConnecting: false,
          //     isConnected: false,
          //     error: 'Connection failed - check network',
          //     connectionAttempts: prev.connectionAttempts + 1
          //   }));
          //   break;
          default:
            break;
        }
      };
    }
  }, []);

  // Fetch VoIP credentials and configuration
  const fetchCredentials = useCallback(async () => {
    if (!decodedToken?.user?.id) return;

    setIsLoadingCredentials(true);
    setCredentialsError(null);

    try {
      console.log('Fetching FreePBX integration...');
      const { data } = await axiosInstance.get<Integration>(
        '/integrations/find-by-key?key=freepbx'
      );

      if (!data.json_value) {
        console.log('No FreePBX configuration found - softphone will be hidden');
        setHasFreePBX(false);
        throw new Error('No VoIP configuration found');
      }

      // FreePBX is available
      setHasFreePBX(true);

      const { extensions, recording_url } = data.json_value as {
        extensions?: Array<{ user_id: number; extension_id: string; password: string }>;
        recording_url?: string;
        client_id?: string;
        client_secret?: string;
        grant_type?: string;
        server_url?: string;
      };

      // Find user's extension
      console.log('Looking for extension for user ID:', decodedToken.user.id);
      console.log('Available extensions:', extensions);

      const userExtension = extensions?.find((ext) => ext.user_id === decodedToken.user.id);

      if (!userExtension) {
        console.error('No extension found for user ID:', decodedToken.user.id);
        setHasFreePBX(false);
        throw new Error('No SIP extension found for this user');
      }

      console.log('Found user extension:', userExtension);

      // Create SIP configuration - matching legacy format exactly
      const domain = 'wss.auttom.io';
      const wsServer = 'wss://wss.auttom.io:8089/ws';

      const config: SoftPhoneConfig = {
        domain: domain,
        uri: `sip:${userExtension.extension_id}@${domain}`,
        password: userExtension.password,
        ws_servers: wsServer,
        display_name: userExtension.extension_id, // Use extension ID as display name like legacy
        register: true,
        authorization_user: userExtension.extension_id,
        session_timers: false,
        use_preloaded_route: false,
        // Explicitly set optional fields to avoid undefined issues
        instance_id: undefined,
        contact_uri: undefined,
      };

      console.log('SIP Configuration created:', {
        domain: config.domain,
        uri: config.uri,
        authorization_user: config.authorization_user,
        ws_servers: config.ws_servers,
        display_name: config.display_name,
        register: config.register,
        contact_uri: config.contact_uri,
        instance_id: config.instance_id,
        session_timers: config.session_timers,
        use_preloaded_route: config.use_preloaded_route,
        // Don't log password for security
        hasPassword: !!config.password,
        passwordLength: config.password?.length
      });

      setSipConfig(config);
      setUsername(userExtension.extension_id);
      setApiKey(data.value || '');
      setRecordingUrl(recording_url || '');

      // Auto-connect if we have valid config
      if (flowControlRef.current && config) {
        try {
          console.log('Initializing CallsFlowControl with config...');
          console.log('Setting flowControlRef.current.config to:', config);
          flowControlRef.current.config = config;

          console.log('Calling flowControlRef.current.init()...');
          flowControlRef.current.init();
          console.log('CallsFlowControl initialized successfully');

          // Verify the config was set correctly
          console.log('Verification - flowControlRef.current.config after init:', flowControlRef.current.config);
        } catch (error) {
          console.error('Failed to initialize CallsFlowControl:', error);
          console.error('Error details:', {
            name: error instanceof Error ? error.name : 'Unknown',
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : 'No stack trace'
          });
          setCredentialsError(`Failed to initialize SIP: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } else {
        console.warn('Skipping CallsFlowControl initialization:', {
          hasFlowControl: !!flowControlRef.current,
          hasConfig: !!config,
          flowControlRef: flowControlRef.current,
          config
        });
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load VoIP credentials';
      setCredentialsError(errorMessage);
      //console.error('Failed to fetch VoIP credentials:', error);

      // Set hasFreePBX to false on error
      setHasFreePBX(false);

      // Only show error toast if it's not a "no configuration" error
      // (silently hide softphone for companies without FreePBX)
      if (!errorMessage.includes('No VoIP configuration') && !errorMessage.includes('No SIP extension')) {
        // Get user-friendly error message
        const errorInfo = getSipErrorInfo(error as Error);

        // Show user-friendly toast notification
        toast.error(errorInfo.title, {
          description: `${errorInfo.message}${errorInfo.action ? '\n\n' + errorInfo.action : ''}`,
          duration: 8000,
        });
      }
    } finally {
      setIsLoadingCredentials(false);
    }
  }, [decodedToken]);

  // Auto-fetch credentials when user is available
  useEffect(() => {
    if (decodedToken?.user?.id) {
      console.log('Fetching SIP credentials for user:', decodedToken.user.id);
      fetchCredentials();
    }
  }, [decodedToken, fetchCredentials]);

  const connect = useCallback(() => {
    if (!flowControlRef.current || !sipConfig) {
      setStatus(prev => ({
        ...prev,
        error: 'No SIP configuration available'
      }));
      return;
    }

    if (status.isConnecting || status.isConnected) {
      return; // Already connecting or connected
    }

    setStatus(prev => ({
      ...prev,
      isConnecting: true,
      error: null,
      connectionAttempts: prev.connectionAttempts + 1
    }));

    try {
      flowControlRef.current.start();
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        isConnecting: false,
        error: error instanceof Error ? error.message : 'Connection failed'
      }));
    }
  }, [sipConfig, status.isConnecting, status.isConnected]);

  // Auto-connect when credentials are loaded
  useEffect(() => {
    console.log('Auto-connect effect triggered:', {
      hasSipConfig: !!sipConfig,
      hasFlowControl: !!flowControlRef.current,
      isConnecting: status.isConnecting,
      isConnected: status.isConnected,
      shouldAutoConnect: sipConfig && flowControlRef.current && !status.isConnecting && !status.isConnected
    });

    if (sipConfig && flowControlRef.current && !status.isConnecting && !status.isConnected) {
      console.log('Calling auto-connect...');
      connect();
    }
  }, [sipConfig, connect, status.isConnecting, status.isConnected]);

  const disconnect = useCallback(() => {
    if (!flowControlRef.current) return;

    try {
      console.log('SipContext: Disconnecting SIP...');

      // Hang up all active calls first
      if (flowControlRef.current.activeCall) {
        console.log('SipContext: Hanging up active call');
        flowControlRef.current.hungup(flowControlRef.current.activeCall.id);
      }

      // Hang up all calls in queue
      flowControlRef.current.callsQueue?.forEach(call => {
        console.log('SipContext: Hanging up queued call:', call.id);
        flowControlRef.current!.hungup(call.id);
      });

      // Hang up all held calls
      flowControlRef.current.holdCallsQueue?.forEach(call => {
        console.log('SipContext: Hanging up held call:', call.id);
        flowControlRef.current!.hungup(call.id);
      });

      // Stop the UA (unregisters and disconnects)
      flowControlRef.current.stop();
      console.log('SipContext: UA stopped');

      setStatus({
        isConnecting: false,
        isConnected: false,
        isRegistered: false,
        error: null,
        connectionAttempts: 0,
      });

      console.log('SipContext: Disconnect complete');
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  }, []);

  const refreshCredentials = useCallback(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const makeCall = useCallback((number: string) => {
    if (!flowControlRef.current) {
      console.error('No flow control available for making calls');
      return;
    }

    if (!status.isConnected || !status.isRegistered) {
      console.error('SIP not connected/registered, cannot make call');
      return;
    }

    try {
      flowControlRef.current.call(number);
    } catch (error) {
      console.error('Error making call:', error);
    }
  }, [status.isConnected, status.isRegistered]);

  const endCall = useCallback(() => {
    if (!flowControlRef.current?.activeCall?.id) {
      console.warn('No active call to end');
      return;
    }

    try {
      flowControlRef.current.hungup(flowControlRef.current.activeCall.id);
      console.log('Call ended successfully');
    } catch (error) {
      console.error('Error ending call:', error);
    }
  }, []);

  const answerCall = useCallback((sessionId: string) => {
    if (!flowControlRef.current) {
      console.error('No flow control available for answering calls');
      return;
    }

    try {
      flowControlRef.current.answer(sessionId);
      console.log('Call answered:', sessionId);
    } catch (error) {
      console.error('Error answering call:', error);
    }
  }, []);

  const rejectCall = useCallback((sessionId: string) => {
    if (!flowControlRef.current) {
      console.error('No flow control available for rejecting calls');
      return;
    }

    try {
      flowControlRef.current.hungup(sessionId);
      console.log('Call rejected:', sessionId);
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (flowControlRef.current) {
      flowControlRef.current.setMicMuted();
      console.log('Toggled mute');
    }
  }, []);

  const mute = useCallback(() => {
    if (flowControlRef.current && !flowControlRef.current.micMuted) {
      flowControlRef.current.setMicMuted();
    }
  }, []);

  const unmute = useCallback(() => {
    if (flowControlRef.current && flowControlRef.current.micMuted) {
      flowControlRef.current.setMicMuted();
    }
  }, []);

  const holdCall = useCallback((sessionId: string) => {
    if (!flowControlRef.current) {
      console.warn('No flow control available');
      return;
    }

    try {
      flowControlRef.current.hold(sessionId);
      console.log('Call placed on hold:', sessionId);
    } catch (error) {
      console.error('Error holding call:', error);
    }
  }, []);

  const unholdCall = useCallback((sessionId: string) => {
    if (!flowControlRef.current) {
      console.warn('No flow control available');
      return;
    }

    try {
      flowControlRef.current.unhold(sessionId);
      console.log('Call resumed from hold:', sessionId);
    } catch (error) {
      console.error('Error unholding call:', error);
    }
  }, []);

  const hold = useCallback(() => {
    if (!flowControlRef.current?.activeCall?.id) {
      console.warn('No active call to hold');
      return;
    }

    const activeCall = flowControlRef.current.activeCall;

    // Check if already on hold
    if (activeCall.isOnHold().local) {
      console.log('Call is already on hold');
      return;
    }

    console.log('Holding active call:', activeCall.id);

    try {
      // Use the session's hold method directly
      activeCall.hold();
      console.log('Call placed on hold successfully');
    } catch (error) {
      console.error('Error holding call:', error);
    }
  }, []);

  const unhold = useCallback(() => {
    if (!flowControlRef.current) return;

    console.log('Unhold called - checking for held calls...');
    console.log('Hold calls queue:', flowControlRef.current.holdCallsQueue?.length || 0);
    console.log('Active call:', flowControlRef.current.activeCall?.id);

    console.log(flowControlRef.current.holdCallsQueue )

    // Check if there are any calls in the hold queue
    if (flowControlRef.current.holdCallsQueue.length > 0) {
      const heldCall = flowControlRef.current.holdCallsQueue[0];
      console.log('Unholding held call:', heldCall.id);

      try {
        // Use the session's unhold method directly
        heldCall.unhold();
        console.log('Call resumed from hold successfully');
      } catch (error) {
        console.error('Error unholding call:', error);
      }
    } else if (flowControlRef.current.activeCall?.id) {
      // If there's an active call that might be on hold
      const activeCall = flowControlRef.current.activeCall;
      console.log('Checking if active call is on hold:', activeCall.id);

      try {
        // Check if the call is on hold using the isOnHold method
        if (activeCall.isOnHold && activeCall.isOnHold()) {
          console.log('Active call is on hold, unholding...');
          activeCall.unhold();
          console.log('Unhold operation initiated');
        } else {
          console.warn('Active call is not on hold');
        }
      } catch (error) {
        console.error('Error unholding active call:', error);
      }
    } else {
      console.warn('No calls to unhold');
    }
  }, []);

  const setCallVolume = useCallback((volume: number) => {
    if (flowControlRef.current?.player?.current) {
      flowControlRef.current.player.current.volume = Math.max(0, Math.min(1, volume));
    }
  }, []);

  const setRingVolume = useCallback((volume: number) => {
    if (flowControlRef.current?.ringer?.current) {
      flowControlRef.current.ringer.current.volume = Math.max(0, Math.min(1, volume));
    }
    if (flowControlRef.current?.ringbackTone) {
      flowControlRef.current.ringbackTone.volume = Math.max(0, Math.min(1, volume));
    }
  }, []);

  const initializeAudio = useCallback(async () => {
    try {
      // Request microphone permissions first
      console.log('Requesting microphone permissions...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      console.log('Microphone permission granted');

      // Stop the test stream immediately - we just needed permissions
      stream.getTracks().forEach(track => track.stop());

      // Try to play and immediately pause audio to initialize audio context
      if (flowControlRef.current?.ringer?.current) {
        const ringer = flowControlRef.current.ringer.current;
        ringer.muted = true;
        await ringer.play();
        ringer.pause();
        ringer.currentTime = 0;
        ringer.muted = false;
        console.log('Audio context initialized for ringtone');
      }

      if (flowControlRef.current?.ringbackTone) {
        const ringback = flowControlRef.current.ringbackTone;
        ringback.muted = true;
        await ringback.play();
        ringback.pause();
        ringback.currentTime = 0;
        ringback.muted = false;
        console.log('Audio context initialized for ringback');
      }

      console.log('Audio and microphone initialization completed successfully');
    } catch (error) {
      console.error('Audio/microphone initialization failed:', error);

      // Get user-friendly error message
      const errorInfo = getSipErrorInfo(error as Error);

      // Show user-friendly toast notification with action button
      toast.error(errorInfo.title, {
        description: errorInfo.message,
        action: errorInfo.action ? {
          label: 'Help',
          onClick: () => {
            // Could open a help modal or link to documentation
            toast.info('Quick Fix', {
              description: errorInfo.action,
              duration: 10000,
            });
          },
        } : undefined,
        duration: 10000,
      });

      throw error; // Re-throw to let caller handle
    }
  }, []);

  // Handle user logout - disconnect SIP
  useEffect(() => {
    // If user token is removed (logout), disconnect
    if (!decodedToken?.user?.id && flowControlRef.current) {
      console.log('User logged out - disconnecting SIP');
      disconnect();
      setSipConfig(null);
      setUsername('');
      setApiKey('');
      setRecordingUrl('');
      setCredentialsError(null);
    }
  }, [decodedToken, disconnect]);

  const value: SipContextType = {
    status,
    sipConfig,
    username,
    apiKey,
    recordingUrl,
    hasFreePBX,
    connect,
    disconnect,
    refreshCredentials,
    flowControl: flowControlRef.current,
    makeCall,
    endCall,
    answerCall,
    rejectCall,
    setCallVolume,
    setRingVolume,
    initializeAudio,
    isLoadingCredentials,
    credentialsError,
    mute,
    unmute,
    toggleMute,
    hold,
    unhold,
    holdCall,
    unholdCall
  };

  return (
    <SipContext.Provider value={value}>
      {children}
    </SipContext.Provider>
  );
};